/**
 * Cron de 5 campos — `minuto hora dia mês dia-da-semana` —, no fuso local.
 *
 * Feito aqui, e não com biblioteca, porque a API e a tela precisam da MESMA
 * conta: a tela mostra "próxima execução" enquanto o admin digita, e se as
 * duas pontas interpretassem o cron diferente a tela prometeria um horário
 * que a agenda não cumpre. O processo da API roda em America/Sao_Paulo
 * (`fusoHorario.ts`), e o navegador da operação também.
 *
 * Aceita `*`, número, lista (`1,15`), faixa (`8-18`) e passo (`*\/5`,
 * `8-18/2`). Dia-da-semana 0 ou 7 é domingo. Como no cron clássico, se dia do
 * mês E dia da semana vierem restritos, basta um dos dois casar.
 */

interface CronInterpretado {
  minutos: Set<number>;
  horas: Set<number>;
  dias: Set<number>;
  meses: Set<number>;
  semana: Set<number>;
  diaLivre: boolean;
  semanaLivre: boolean;
}

const LIMITES: [number, number][] = [
  [0, 59],
  [0, 23],
  [1, 31],
  [1, 12],
  [0, 7],
];

function campo(texto: string, [min, max]: [number, number]): Set<number> {
  const valores = new Set<number>();
  for (const parte of texto.split(',')) {
    const [faixa, passoTexto] = parte.split('/');
    const passo = passoTexto === undefined ? 1 : Number(passoTexto);
    if (!Number.isInteger(passo) || passo < 1) throw new Error(`passo inválido em "${parte}"`);

    let inicio: number;
    let fim: number;
    if (faixa === '*') {
      inicio = min;
      fim = max;
    } else if (faixa?.includes('-')) {
      const [a, b] = faixa.split('-').map(Number);
      inicio = a as number;
      fim = b as number;
    } else {
      inicio = Number(faixa);
      fim = passoTexto === undefined ? inicio : max;
    }
    if (!Number.isInteger(inicio) || !Number.isInteger(fim) || inicio < min || fim > max || inicio > fim) {
      throw new Error(`valor fora de ${min}-${max} em "${parte}"`);
    }
    for (let v = inicio; v <= fim; v += passo) valores.add(v);
  }
  return valores;
}

export function interpretarCron(expressao: string): CronInterpretado {
  const partes = expressao.trim().split(/\s+/);
  if (partes.length !== 5) {
    throw new Error('O cron precisa de 5 campos: minuto hora dia mês dia-da-semana.');
  }
  const [mi, ho, di, me, se] = partes as [string, string, string, string, string];
  const semana = campo(se, LIMITES[4] as [number, number]);
  if (semana.has(7)) semana.add(0);
  return {
    minutos: campo(mi, LIMITES[0] as [number, number]),
    horas: campo(ho, LIMITES[1] as [number, number]),
    dias: campo(di, LIMITES[2] as [number, number]),
    meses: campo(me, LIMITES[3] as [number, number]),
    semana,
    diaLivre: di === '*',
    semanaLivre: se === '*',
  };
}

/** `null` quando é válido; senão a frase do erro. */
export function validarCron(expressao: string): string | null {
  try {
    interpretarCron(expressao);
    return proximaExecucaoCron(expressao, new Date()) ? null : 'Este cron nunca dispara.';
  } catch (err) {
    return err instanceof Error ? err.message : 'Cron inválido.';
  }
}

/**
 * O primeiro minuto DEPOIS de `apos` em que o cron dispara.
 *
 * Anda pulando: mês que não casa vai para o dia 1 do seguinte, dia que não
 * casa vai para a meia-noite seguinte, hora que não casa vai para a próxima
 * hora. Assim até um "todo 29 de fevereiro" resolve em poucas voltas.
 */
export function proximaExecucaoCron(expressao: string, apos: Date): Date | null {
  const c = interpretarCron(expressao);
  const d = new Date(apos.getTime());
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() + 1);

  const limite = apos.getTime() + 366 * 24 * 3_600_000 * 5;
  while (d.getTime() <= limite) {
    if (!c.meses.has(d.getMonth() + 1)) {
      d.setMonth(d.getMonth() + 1, 1);
      d.setHours(0, 0, 0, 0);
      continue;
    }
    const casaDia = c.dias.has(d.getDate());
    const casaSemana = c.semana.has(d.getDay());
    const diaOk =
      c.diaLivre && c.semanaLivre
        ? true
        : c.diaLivre
          ? casaSemana
          : c.semanaLivre
            ? casaDia
            : casaDia || casaSemana;
    if (!diaOk) {
      d.setDate(d.getDate() + 1);
      d.setHours(0, 0, 0, 0);
      continue;
    }
    if (!c.horas.has(d.getHours())) {
      d.setHours(d.getHours() + 1, 0, 0, 0);
      continue;
    }
    if (!c.minutos.has(d.getMinutes())) {
      d.setMinutes(d.getMinutes() + 1, 0, 0);
      continue;
    }
    return d;
  }
  return null;
}

const DIAS_SEMANA = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

/** O cron em português, para os casos comuns; senão, o próprio cron. */
export function descreverCron(expressao: string): string {
  const p = expressao.trim().split(/\s+/);
  if (p.length !== 5) return expressao;
  const [mi, ho, di, me, se] = p as [string, string, string, string, string];
  const doisDigitos = (n: string): string => n.padStart(2, '0');

  if (mi === '*' && ho === '*' && di === '*' && me === '*' && se === '*') return 'A cada minuto';
  const passoMin = /^\*\/(\d+)$/.exec(mi);
  if (passoMin && ho === '*' && di === '*' && me === '*' && se === '*') {
    return `A cada ${passoMin[1]} minutos`;
  }
  if (/^\d+$/.test(mi) && di === '*' && me === '*' && se === '*') {
    if (ho === '*') return mi === '0' ? 'De hora em hora' : `De hora em hora, no minuto ${mi}`;
    const passoHora = /^\*\/(\d+)$/.exec(ho);
    if (passoHora) return `A cada ${passoHora[1]} horas`;
    if (/^\d+$/.test(ho)) {
      return ho === '0' && mi === '0'
        ? 'Todo dia à meia-noite'
        : `Todo dia às ${doisDigitos(ho)}:${doisDigitos(mi)}`;
    }
  }
  if (/^\d+$/.test(mi) && /^\d+$/.test(ho) && di === '*' && me === '*' && /^\d$/.test(se)) {
    return `Toda ${DIAS_SEMANA[Number(se) % 7]} às ${doisDigitos(ho)}:${doisDigitos(mi)}`;
  }
  return expressao;
}
